import React, { useCallback, useEffect, useState } from 'react';
import { Star, Quote, ChevronDown, ChevronUp, PenLine } from 'lucide-react';
import { useTranslation } from '../context/TranslationContext';
import reviewText from '../data/reviewText';
import { displayBookName } from '../utils/bookGrouping';
import ReviewForm from './ReviewForm';

var BACKEND = process.env.REACT_APP_BACKEND_URL;

var APP_NAME = { forscore: 'forScore', mobilesheets: 'MobileSheets' };

var THEME = {
  forscore: { accent: 'sky', text: 'text-sky-400', fill: 'fill-sky-400', ring: 'border-sky-500/30', glow: 'from-sky-500/10' },
  mobilesheets: { accent: 'emerald', text: 'text-emerald-400', fill: 'fill-emerald-400', ring: 'border-emerald-500/30', glow: 'from-emerald-500/10' }
};

function Stars(props) {
  var items = [1, 2, 3, 4, 5].map(function(s) {
    var on = s <= Math.round(props.value || 0);
    return <Star key={s} className={(props.size || 'w-4 h-4') + ' ' + (on ? props.fill + ' ' + props.text : 'text-neutral-700')} />;
  });
  return <span className="inline-flex items-center gap-0.5">{items}</span>;
}

// The app-wide review block on /forscore and /mobilesheets: a quiet summary bar
// that any visitor can read at a glance, plus one place to review the indexed
// files as a whole instead of a single volume.
export default function PlatformReviews(props) {
  var platform = props.platform;
  var theme = THEME[platform] || THEME.forscore;
  var langRef = useTranslation();
  var tx = reviewText[langRef.language] || reviewText.en;

  var dataState = useState({ reviews: [], count: 0, average: 0, general_slug: '' });
  var data = dataState[0];
  var setData = dataState[1];
  var [cursor, setCursor] = useState(0);
  var [fading, setFading] = useState(false);
  var [showAll, setShowAll] = useState(false);
  var [showForm, setShowForm] = useState(false);

  var load = useCallback(function() {
    fetch(BACKEND + '/api/reviews/platform/' + platform)
      .then(function(r) { return r.json(); })
      .then(function(d) { setData(d || { reviews: [] }); })
      .catch(function() {});
  }, [platform]);

  useEffect(function() { load(); }, [load]);

  var reviews = data.reviews || [];

  useEffect(function() {
    if (reviews.length < 2) return undefined;
    var timer = setInterval(function() {
      setFading(true);
      setTimeout(function() {
        setCursor(function(c) { return (c + 1) % reviews.length; });
        setFading(false);
      }, 320);
    }, 6500);
    return function() { clearInterval(timer); };
  }, [reviews.length]);

  function labelFor(review) {
    if (review.general_platform) {
      return tx.generalLabel.replace('{app}', APP_NAME[review.general_platform] || '');
    }
    if (!review.product_name) return '';
    return displayBookName({ name: review.product_name, slug: review.product_slug, platform: platform });
  }

  var current = reviews[cursor] || null;
  var countLabel = data.count + ' ' + (data.count === 1 ? tx.one : tx.many);

  // Nothing yet: a quiet invitation rather than an empty 0-star scoreboard.
  if (!data.count) {
    return (
      <div id="reviews" className="mb-12" data-testid={'platform-reviews-' + platform}>
        <div className={'rounded-2xl border ' + theme.ring + ' bg-neutral-900/40 px-5 py-4 flex flex-wrap items-center gap-3'}>
          <p className="flex-1 min-w-[200px] text-neutral-400 text-sm">{tx.stripEmpty}</p>
          <button onClick={function() { setShowForm(!showForm); }}
                  data-testid="platform-reviews-write"
                  className={'inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-4 py-2 transition-colors ' + (platform === 'mobilesheets' ? 'bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25' : 'bg-sky-500/15 text-sky-300 hover:bg-sky-500/25')}>
            <PenLine className="w-3.5 h-3.5" /> {tx.stripWrite}
          </button>
        </div>
        {showForm ? (
          <div className="mt-4">
            <ReviewForm productSlug={data.general_slug || (platform + '-indexed-files')}
                        accent={theme.accent} title={tx.generalTitle}
                        subtitle={tx.generalSubtitle} onSubmitted={load} />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div id="reviews" className="mb-12" data-testid={'platform-reviews-' + platform}>
      <div className={'relative overflow-hidden rounded-2xl border ' + theme.ring + ' bg-neutral-900/60 backdrop-blur-xl'}>
        <div className={'absolute inset-0 bg-gradient-to-r ' + theme.glow + ' to-transparent pointer-events-none'} />
        <div className="relative px-5 py-5 sm:px-7 sm:py-6 flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="shrink-0">
            <p className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1.5">{tx.stripKicker}</p>
            <div className="flex items-baseline gap-2">
              <span className={'text-3xl font-bold ' + theme.text} data-testid="platform-reviews-average">
                {data.count ? data.average : '—'}
              </span>
              <Stars value={data.average} fill={theme.fill} text={theme.text} size="w-4 h-4" />
            </div>
            <p className="text-neutral-400 text-xs mt-1.5" data-testid="platform-reviews-count">{countLabel}</p>
          </div>

          <div className="hidden sm:block w-px self-stretch bg-white/10" />

          <div className="flex-1 min-w-0">
            {current ? (
              <div className={'transition-opacity duration-300 ' + (fading ? 'opacity-0' : 'opacity-100')}
                   data-testid="platform-reviews-quote">
                <p className="text-neutral-200 text-sm leading-relaxed line-clamp-3">
                  <Quote className={'w-3.5 h-3.5 inline-block mr-1.5 -translate-y-0.5 ' + theme.text} />
                  {current.text}
                </p>
                <p className="text-neutral-500 text-xs mt-2">
                  {current.name}
                  {labelFor(current) ? <span className="text-neutral-600"> · {labelFor(current)}</span> : null}
                </p>
              </div>
            ) : (
              <p className="text-neutral-400 text-sm">{tx.stripEmpty}</p>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {reviews.length ? (
              <button onClick={function() { setShowAll(!showAll); }}
                      data-testid="platform-reviews-toggle"
                      className="inline-flex items-center gap-1 text-xs text-neutral-300 hover:text-white border border-white/15 hover:border-white/35 rounded-full px-3.5 py-2 transition-colors">
                {showAll ? tx.stripHide : tx.stripSeeAll}
                {showAll ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
            ) : null}
            <button onClick={function() { setShowForm(!showForm); }}
                    data-testid="platform-reviews-write"
                    className={'inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-4 py-2 transition-colors ' + (platform === 'mobilesheets' ? 'bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25' : 'bg-sky-500/15 text-sky-300 hover:bg-sky-500/25')}>
              <PenLine className="w-3.5 h-3.5" /> {tx.stripWrite}
            </button>
          </div>
        </div>
      </div>

      {showAll && reviews.length ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4" data-testid="platform-reviews-list">
          {reviews.map(function(r, i) {
            return (
              <div key={i} className="bg-neutral-900/70 border border-white/5 rounded-xl p-5">
                <Stars value={r.rating} fill={theme.fill} text={theme.text} size="w-3.5 h-3.5" />
                <p className="text-neutral-300 text-sm leading-relaxed mt-3">{'"' + r.text + '"'}</p>
                {r.reply ? (
                  <p className="text-neutral-400 text-xs mt-3 border-l-2 border-white/10 pl-3">
                    <span className={theme.text}>{tx.reply}:</span> {r.reply}
                  </p>
                ) : null}
                <div className="flex items-center justify-between gap-3 mt-4">
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium truncate">{r.name}</p>
                    <p className="text-neutral-600 text-xs">{r.date}</p>
                  </div>
                  {labelFor(r) ? (
                    <span className="text-[10px] text-neutral-500 bg-neutral-800 rounded px-2 py-1 truncate max-w-[50%]">
                      {tx.reviewFor}: {labelFor(r)}
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {showForm ? (
        <div className="mt-4">
          <ReviewForm productSlug={data.general_slug || (platform + '-indexed-files')}
                      accent={theme.accent}
                      title={tx.generalTitle}
                      subtitle={tx.generalSubtitle}
                      onSubmitted={load} />
        </div>
      ) : null}
    </div>
  );
}
